import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import { attachSessionGuard } from '../sessionGuard';
import { CheckCircle, XCircle, Save, ArrowLeft, RefreshCw } from 'lucide-react';

// ── API setup ──────────────────────────────────────────────────────────────────
const API = 'https://api.jamboricg.com/api/guliohub';

const authHeader = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('token');
  return token ? { Authorization: `Token ${token}` } : {};
};

const api = axios.create({ baseURL: API });
// Four-hour session expiry, same rule as everywhere else.
attachSessionGuard(api);

api.interceptors.request.use(cfg => {
  cfg.headers = { ...cfg.headers, ...authHeader() };
  return cfg;
});

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmtDate = v => {
  if (!v) return '—';
  return new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const ownershipLabel = {
  sole: 'Sole Ownership',
  partnership: 'Partnership / Shared Holders',
  company: 'Registered Company',
  business: 'Business',
};

// ── Small UI atoms ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const map = {
    verified:  { label: 'Verified',  cls: 'bg-emerald-100 text-emerald-700' },
    active:    { label: 'Active',    cls: 'bg-emerald-100 text-emerald-700' },
    pending:   { label: 'Pending',   cls: 'bg-amber-100 text-amber-700' },
    rejected:  { label: 'Rejected',  cls: 'bg-red-100 text-red-700' },
    suspended: { label: 'Suspended', cls: 'bg-orange-100 text-orange-700' },
  };
  const m = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600' };
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-bold ${m.cls}`}>{m.label}</span>
  );
};

const Btn = ({ children, variant = 'ghost', disabled, onClick, className = '' }) => {
  const base = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors cursor-pointer border';
  const variants = {
    success: 'bg-emerald-500 hover:bg-emerald-600 text-white border-transparent',
    ghost:   'bg-slate-100 hover:bg-slate-200 text-slate-700 border-transparent',
    outline: 'bg-white hover:bg-slate-50 text-slate-700 border-slate-200',
    danger:  'bg-red-100 hover:bg-red-200 text-red-700 border-transparent',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${variants[variant] || variants.ghost} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}
    >
      {children}
    </button>
  );
};

const Toast = ({ msg, type }) => {
  if (!msg) return null;
  return (
    <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl shadow-lg text-sm font-semibold text-white transition-all
      ${type === 'error' ? 'bg-red-500' : 'bg-emerald-500'}`}>
      {msg}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-5 mb-4">
    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-slate-400 font-medium mb-0.5">{label}</p>
    <p className={`font-semibold text-slate-700 ${mono ? 'font-mono text-sm' : ''}`}>{value || '—'}</p>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────
export default function SellerDetailPage() {
  const navigate        = useNavigate();
  const location        = useLocation();
  const { id }          = useParams();

  const [seller,  setSeller]  = useState(location.state?.seller || null);
  const [loading, setLoading] = useState(!seller);
  const [saving,  setSaving]  = useState(false);
  const [notes,   setNotes]   = useState('');
  const [toast,   setToast]   = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  // If navigated directly by URL (no state), fetch the sellers list and find by id
  useEffect(() => {
    if (seller) {
      setNotes(seller.admin_notes || '');
      return;
    }
    setLoading(true);
    api.get('/admin/sellers/')
      .then(r => {
        const found = r.data.find(s => String(s.id) === String(id));
        if (found) {
          setSeller(found);
          setNotes(found.admin_notes || '');
        } else {
          showToast('Seller not found.', 'error');
        }
      })
      .catch(() => showToast('Failed to load seller.', 'error'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patch = async payload => {
    setSaving(true);
    try {
      const r = await api.patch(`/admin/sellers/${seller.id}/`, payload);
      setSeller(prev => ({ ...prev, ...payload, ...r.data }));
      if (payload.admin_notes !== undefined) setNotes(payload.admin_notes);
      showToast('Seller updated.');
    } catch (e) {
      showToast(e.response?.data?.detail || 'Update failed.', 'error');
    } finally { setSaving(false); }
  };

  // ── Loading state ────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!seller) {
    return (
      <div className="p-8 text-center text-slate-400">
        <p className="text-lg font-semibold mb-3">Seller not found</p>
        <Btn variant="ghost" onClick={() => navigate('/guliohub')}>
          <ArrowLeft size={13} /> Back to GulioHub
        </Btn>
      </div>
    );
  }

  // ── Detail page ──────────────────────────────────────────────────────────────
  return (
    <div className="p-6">
      <Toast msg={toast?.msg} type={toast?.type} />

      {/* Header breadcrumb bar */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/guliohub')}
          className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          <ArrowLeft size={15} /> Back to GulioHub
        </button>
        <span className="text-slate-300">/</span>
        <span className="text-sm text-slate-400">Sellers</span>
        <span className="text-slate-300">/</span>
        <span className="text-sm font-bold text-slate-800">{seller.business_name}</span>
        <StatusBadge status={seller.status} />
        {seller.is_registered && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
            ✔ Registered Business
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

        {/* ── Left column ───────────────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          <Section title="Business Information">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Business Name"  value={seller.business_name} />
              <Field label="Business Type"  value={seller.business_type} />
              <Field label="Ownership Type" value={ownershipLabel[seller.ownership_type] || seller.ownership_type} />
              <Field label="Region"         value={seller.region} />
              <Field label="Sell Type"      value={seller.sell_type} />
              <Field label="Applied On"     value={fmtDate(seller.created_at)} />
            </div>
          </Section>

          <Section title="Contact Details">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Account Name"   value={seller.user_name} />
              <Field label="Account Email"  value={seller.user_email} />
              <Field label="Business Phone" value={seller.phone} />
              <Field label="Business Email" value={seller.email} />
              <div className="col-span-2">
                <Field label="Address" value={seller.address} />
              </div>
            </div>
          </Section>

          <Section title="Business Registration">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <Field label="Officially Registered" value={seller.is_registered ? 'Yes' : 'No'} />
              <Field label="Registration Number"   value={seller.registration_number} mono />
            </div>

            {seller.licence_url ? (
              <div>
                <p className="text-xs text-slate-400 font-medium mb-2">Business Licence / Certificate</p>
                {/\.(jpg|jpeg|png|gif|webp)$/i.test(seller.licence_url) ? (
                  <a href={seller.licence_url} target="_blank" rel="noreferrer">
                    <img
                      src={seller.licence_url}
                      alt="Business Licence"
                      className="max-w-sm rounded-lg border border-slate-200 shadow-sm hover:opacity-90 transition-opacity cursor-pointer"
                      style={{ maxHeight: 300, objectFit: 'contain' }}
                    />
                    <p className="text-xs text-teal-600 mt-1 font-medium">Click to open full size ↗</p>
                  </a>
                ) : (
                  <a
                    href={seller.licence_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors"
                  >
                    📄 Download / View Licence ↗
                  </a>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-400 italic">No licence uploaded</p>
            )}
          </Section>

          {seller.bank_accounts?.length > 0 && (
            <Section title={`Bank Accounts (${seller.bank_accounts.length})`}>
              <div className="space-y-3">
                {seller.bank_accounts.map((b, i) => (
                  <div key={b.id || i} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-700 font-bold text-xs">
                      {b.is_primary ? '★' : i + 1}
                    </div>
                    <div className="grid grid-cols-3 gap-3 flex-1 text-sm">
                      <div>
                        <p className="text-xs text-slate-400">Bank</p>
                        <p className="font-bold text-slate-700">{b.bank_name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Account Number</p>
                        <p className="font-mono font-semibold text-slate-700">{b.account_number}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-400">Account Holder</p>
                        <p className="font-semibold text-slate-700">{b.account_holder}</p>
                      </div>
                    </div>
                    {b.is_primary && (
                      <span className="text-xs font-bold text-blue-600 bg-blue-100 px-2 py-0.5 rounded-full flex-shrink-0">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </Section>
          )}

        </div>

        {/* ── Right column ──────────────────────────────────────────────────── */}
        <div className="space-y-5">

          <Section title="Status">
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400 mb-1">Current Status</p>
                <StatusBadge status={seller.status} />
              </div>
              <Field label="Terms Accepted" value={seller.terms_accepted ? 'Yes' : 'No'} />
              <Field label="Verified On"    value={fmtDate(seller.verified_at)} />
              <Field label="Live Products"  value={seller.product_count ?? '—'} />
            </div>
          </Section>

          <Section title="Admin Actions">
            <p className="text-xs text-slate-400 font-medium mb-1">Admin Notes</p>
            <textarea
              rows={4}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Internal notes about this seller…"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-300 resize-none mb-3"
            />
            <div className="flex flex-col gap-2">
              <Btn variant="success" disabled={saving} className="w-full justify-center"
                onClick={() => patch({ status: 'verified', admin_notes: notes })}>
                <CheckCircle size={13} /> Verify Seller
              </Btn>
              <Btn variant="danger" disabled={saving} className="w-full justify-center"
                onClick={() => patch({ status: 'rejected', admin_notes: notes })}>
                <XCircle size={13} /> Reject
              </Btn>
              <Btn variant="outline" disabled={saving} className="w-full justify-center"
                onClick={() => patch({ status: 'suspended', admin_notes: notes })}>
                Suspend
              </Btn>
              <Btn variant="ghost" disabled={saving} className="w-full justify-center"
                onClick={() => patch({ admin_notes: notes })}>
                <Save size={13} /> Save Notes
              </Btn>
            </div>
          </Section>

        </div>
      </div>
    </div>
  );
}
